import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { FaUtensils, FaArrowLeft, FaInfoCircle } from 'react-icons/fa';
import SEO, { PageSEO, StructuredData } from '../../components/SEO';

const MacroCalculator = () => {
  const [unit, setUnit] = useState('metric');
  const [gender, setGender] = useState('male');
  const [age, setAge] = useState('');
  const [height, setHeight] = useState('');
  const [heightFeet, setHeightFeet] = useState('');
  const [heightInches, setHeightInches] = useState('');
  const [weight, setWeight] = useState('');
  const [activityLevel, setActivityLevel] = useState('moderate');
  const [goal, setGoal] = useState('lose');
  const [result, setResult] = useState(null);

  const activityMultipliers = {
    sedentary: 1.2,
    light: 1.375,
    moderate: 1.55,
    active: 1.725,
    extreme: 1.9,
  };

  const goals = [
    { value: 'lose', label: 'Lose Weight', adjustment: -500, proteinMultiplier: 2.2 },
    { value: 'maintain', label: 'Maintain Weight', adjustment: 0, proteinMultiplier: 1.8 },
    { value: 'gain', label: 'Build Muscle', adjustment: 300, proteinMultiplier: 2.4 },
  ];

  const calculateMacros = (e) => {
    e.preventDefault();

    let heightInCm, weightInKg;

    if (unit === 'metric') {
      heightInCm = parseFloat(height);
      weightInKg = parseFloat(weight);
    } else {
      const totalInches = (parseFloat(heightFeet) * 12) + parseFloat(heightInches || 0);
      heightInCm = totalInches * 2.54;
      weightInKg = parseFloat(weight) * 0.453592;
    }

    // Calculate BMR using Mifflin-St Jeor
    let bmr;
    if (gender === 'male') {
      bmr = (10 * weightInKg) + (6.25 * heightInCm) - (5 * parseFloat(age)) + 5;
    } else {
      bmr = (10 * weightInKg) + (6.25 * heightInCm) - (5 * parseFloat(age)) - 161;
    }

    const tdee = bmr * activityMultipliers[activityLevel];
    const selectedGoal = goals.find((g) => g.value === goal);
    const targetCalories = Math.round(tdee + selectedGoal.adjustment);

    // Calculate macros
    const proteinGrams = Math.round(weightInKg * selectedGoal.proteinMultiplier);
    const proteinCalories = proteinGrams * 4;

    // Fat: 25-30% of calories
    const fatCalories = Math.round(targetCalories * 0.28);
    const fatGrams = Math.round(fatCalories / 9);

    // Remaining calories from carbs
    const carbCalories = targetCalories - proteinCalories - fatCalories;
    const carbGrams = Math.round(carbCalories / 4);

    setResult({
      calories: targetCalories,
      protein: { grams: proteinGrams, calories: proteinCalories, percent: Math.round((proteinCalories / targetCalories) * 100) },
      carbs: { grams: carbGrams, calories: carbCalories, percent: Math.round((carbCalories / targetCalories) * 100) },
      fat: { grams: fatGrams, calories: fatCalories, percent: Math.round((fatCalories / targetCalories) * 100) },
      goalLabel: selectedGoal.label,
    });
  };

  const resetCalculator = () => {
    setAge('');
    setHeight('');
    setHeightFeet('');
    setHeightInches('');
    setWeight('');
    setResult(null);
  };

  const macroStructuredData = StructuredData.calculator(
    'Macro Calculator',
    'Get personalized protein, carbohydrates, and fat targets based on your fitness goals.',
    '/calculators/macro'
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO {...PageSEO.macroCalculator} structuredData={macroStructuredData} />
      {/* Header */}
      <section className="bg-gradient-to-br from-green-500 to-emerald-600 text-white py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/calculators" className="inline-flex items-center text-green-100 hover:text-white mb-6 transition-colors">
            <FaArrowLeft className="mr-2" />
            All Calculators
          </Link>
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-white/10 rounded-xl flex items-center justify-center">
              <FaUtensils className="text-2xl" />
            </div>
            <div>
              <h1 className="text-3xl md:text-4xl font-bold">Macro Calculator</h1>
              <p className="text-green-100 mt-1">Get your personalized macronutrient targets</p>
            </div>
          </div>
        </div>
      </section>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid md:grid-cols-2 gap-8">
          {/* Calculator Form */}
          <div className="bg-white rounded-2xl shadow-sm p-6 md:p-8 border border-gray-100">
            <h2 className="text-xl font-bold text-gray-900 mb-6">Enter Your Details</h2>

            {/* Unit Toggle */}
            <div className="flex bg-gray-100 rounded-lg p-1 mb-6">
              <button
                type="button"
                onClick={() => setUnit('metric')}
                className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                  unit === 'metric' ? 'bg-white shadow text-primary-600' : 'text-gray-600'
                }`}
              >
                Metric
              </button>
              <button
                type="button"
                onClick={() => setUnit('imperial')}
                className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                  unit === 'imperial' ? 'bg-white shadow text-primary-600' : 'text-gray-600'
                }`}
              >
                Imperial
              </button>
            </div>

            <form onSubmit={calculateMacros} className="space-y-5">
              {/* Gender */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Gender</label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => setGender('male')}
                    className={`py-3 px-4 rounded-xl border-2 font-medium transition-colors ${
                      gender === 'male'
                        ? 'border-green-500 bg-green-50 text-green-700'
                        : 'border-gray-200 text-gray-600 hover:border-gray-300'
                    }`}
                  >
                    Male
                  </button>
                  <button
                    type="button"
                    onClick={() => setGender('female')}
                    className={`py-3 px-4 rounded-xl border-2 font-medium transition-colors ${
                      gender === 'female'
                        ? 'border-green-500 bg-green-50 text-green-700'
                        : 'border-gray-200 text-gray-600 hover:border-gray-300'
                    }`}
                  >
                    Female
                  </button>
                </div>
              </div>

              {/* Age */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Age</label>
                <input
                  type="number"
                  value={age}
                  onChange={(e) => setAge(e.target.value)}
                  placeholder="e.g., 30"
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  required
                  min="15"
                  max="100"
                />
              </div>

              {/* Height */}
              {unit === 'metric' ? (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Height (cm)</label>
                  <input
                    type="number"
                    value={height}
                    onChange={(e) => setHeight(e.target.value)}
                    placeholder="e.g., 170"
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    required
                    min="100"
                    max="250"
                  />
                </div>
              ) : (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Height</label>
                  <div className="grid grid-cols-2 gap-3">
                    <input
                      type="number"
                      value={heightFeet}
                      onChange={(e) => setHeightFeet(e.target.value)}
                      placeholder="Feet"
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500"
                      required
                      min="3"
                      max="8"
                    />
                    <input
                      type="number"
                      value={heightInches}
                      onChange={(e) => setHeightInches(e.target.value)}
                      placeholder="Inches"
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500"
                      min="0"
                      max="11"
                    />
                  </div>
                </div>
              )}

              {/* Weight */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Weight ({unit === 'metric' ? 'kg' : 'lbs'})
                </label>
                <input
                  type="number"
                  value={weight}
                  onChange={(e) => setWeight(e.target.value)}
                  placeholder={unit === 'metric' ? 'e.g., 70' : 'e.g., 154'}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  required
                  min="30"
                  max={unit === 'metric' ? '300' : '660'}
                />
              </div>

              {/* Activity Level */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Activity Level</label>
                <select
                  value={activityLevel}
                  onChange={(e) => setActivityLevel(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500"
                >
                  <option value="sedentary">Sedentary (little or no exercise)</option>
                  <option value="light">Lightly Active (1-3 days/week)</option>
                  <option value="moderate">Moderately Active (3-5 days/week)</option>
                  <option value="active">Very Active (6-7 days/week)</option>
                  <option value="extreme">Extremely Active (physical job)</option>
                </select>
              </div>

              {/* Goal */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Your Goal</label>
                <div className="grid grid-cols-3 gap-2">
                  {goals.map((g) => (
                    <button
                      key={g.value}
                      type="button"
                      onClick={() => setGoal(g.value)}
                      className={`py-3 px-2 rounded-xl border-2 font-medium text-sm transition-colors ${
                        goal === g.value
                          ? 'border-green-500 bg-green-50 text-green-700'
                          : 'border-gray-200 text-gray-600 hover:border-gray-300'
                      }`}
                    >
                      {g.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  type="submit"
                  className="flex-1 bg-gradient-to-r from-green-500 to-emerald-600 text-white py-3 px-6 rounded-xl hover:from-green-600 hover:to-emerald-700 transition-colors font-semibold"
                >
                  Calculate Macros
                </button>
                {result && (
                  <button
                    type="button"
                    onClick={resetCalculator}
                    className="px-6 py-3 border border-gray-300 rounded-xl hover:bg-gray-50 transition-colors font-medium text-gray-700"
                  >
                    Reset
                  </button>
                )}
              </div>
            </form>
          </div>

          {/* Results */}
          <div className="bg-white rounded-2xl shadow-sm p-6 md:p-8 border border-gray-100">
            <h2 className="text-xl font-bold text-gray-900 mb-6">Your Daily Macros</h2>

            {result ? (
              <div className="space-y-6">
                {/* Total Calories */}
                <div className="text-center py-4 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl text-white">
                  <div className="text-sm text-green-100 mb-1">{result.goalLabel}</div>
                  <div className="text-4xl font-bold">{result.calories}</div>
                  <div className="text-green-100">calories per day</div>
                </div>

                {/* Macro Breakdown */}
                <div className="space-y-4">
                  {/* Protein */}
                  <div className="bg-red-50 rounded-xl p-4">
                    <div className="flex justify-between items-center mb-2">
                      <span className="font-semibold text-red-700">Protein</span>
                      <span className="text-2xl font-bold text-red-700">{result.protein.grams}g</span>
                    </div>
                    <div className="w-full bg-red-200 rounded-full h-3">
                      <div
                        className="bg-red-500 h-3 rounded-full transition-all duration-500"
                        style={{ width: `${result.protein.percent}%` }}
                      ></div>
                    </div>
                    <div className="flex justify-between text-sm text-gray-600 mt-1">
                      <span>{result.protein.calories} cal</span>
                      <span>{result.protein.percent}%</span>
                    </div>
                  </div>

                  {/* Carbs */}
                  <div className="bg-yellow-50 rounded-xl p-4">
                    <div className="flex justify-between items-center mb-2">
                      <span className="font-semibold text-yellow-700">Carbohydrates</span>
                      <span className="text-2xl font-bold text-yellow-700">{result.carbs.grams}g</span>
                    </div>
                    <div className="w-full bg-yellow-200 rounded-full h-3">
                      <div
                        className="bg-yellow-500 h-3 rounded-full transition-all duration-500"
                        style={{ width: `${result.carbs.percent}%` }}
                      ></div>
                    </div>
                    <div className="flex justify-between text-sm text-gray-600 mt-1">
                      <span>{result.carbs.calories} cal</span>
                      <span>{result.carbs.percent}%</span>
                    </div>
                  </div>

                  {/* Fat */}
                  <div className="bg-blue-50 rounded-xl p-4">
                    <div className="flex justify-between items-center mb-2">
                      <span className="font-semibold text-blue-700">Fat</span>
                      <span className="text-2xl font-bold text-blue-700">{result.fat.grams}g</span>
                    </div>
                    <div className="w-full bg-blue-200 rounded-full h-3">
                      <div
                        className="bg-blue-500 h-3 rounded-full transition-all duration-500"
                        style={{ width: `${result.fat.percent}%` }}
                      ></div>
                    </div>
                    <div className="flex justify-between text-sm text-gray-600 mt-1">
                      <span>{result.fat.calories} cal</span>
                      <span>{result.fat.percent}%</span>
                    </div>
                  </div>
                </div>

                {/* CTA */}
                <Link
                  to="/pricing"
                  className="block w-full text-center bg-accent-500 text-white py-3 rounded-xl hover:bg-accent-600 transition-colors font-semibold"
                >
                  Get Custom Meal Plan
                </Link>
              </div>
            ) : (
              <div className="text-center py-12 text-gray-500">
                <FaUtensils className="text-5xl mx-auto mb-4 text-gray-300" />
                <p>Enter your details to calculate your daily macro targets</p>
              </div>
            )}
          </div>
        </div>

        {/* Info Section */}
        <div className="mt-12 bg-white rounded-2xl shadow-sm p-6 md:p-8 border border-gray-100">
          <div className="flex items-center gap-3 mb-4">
            <FaInfoCircle className="text-green-600 text-xl" />
            <h3 className="text-lg font-bold text-gray-900">Understanding Macros</h3>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="p-4 bg-red-50 rounded-xl">
              <h4 className="font-semibold text-red-700 mb-2">Protein</h4>
              <p className="text-sm text-gray-600">
                Essential for muscle repair and growth. Each gram provides 4 calories. Higher protein helps preserve muscle during weight loss.
              </p>
            </div>
            <div className="p-4 bg-yellow-50 rounded-xl">
              <h4 className="font-semibold text-yellow-700 mb-2">Carbohydrates</h4>
              <p className="text-sm text-gray-600">
                Your body's primary energy source. Each gram provides 4 calories. Choose complex carbs for sustained energy.
              </p>
            </div>
            <div className="p-4 bg-blue-50 rounded-xl">
              <h4 className="font-semibold text-blue-700 mb-2">Fats</h4>
              <p className="text-sm text-gray-600">
                Essential for hormone production and nutrient absorption. Each gram provides 9 calories. Focus on healthy fats.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MacroCalculator;
